import dayjs from 'dayjs';
import { isChromeExtension } from '../config';
const storedValuesKeys = [
  'hasSeenWelcomeDoc',
  'hasSeenDescription',
  'extensionPromptSnoozeTill',
  'oktaOpenOnNext',
  'notificationsEnabled',
  'triggers',
  'isExpandedPref',
];

const logAll = async () => {
  if (isChromeExtension) {
    const stored = await chrome.storage.sync.get(null);
    console.log('LOCAL STORAGE: ', stored);
  } else {
    console.log('LOCAL STORAGE: ', window.localStorage);
  }
};

// function to get a value from window local storage
const getValue = async (key) => {
  if (isChromeExtension) {
    const value = await chrome.storage.sync.get(key);
    if (value[key] !== undefined) return value[key];
    else return null;
  } else {
    return window.localStorage.getItem(key);
  }
};

// function to set a value in window local storage
const setValue = async (key, value) => {
  if (isChromeExtension) {
    await chrome.storage.sync.set({ [key]: value });
  } else {
    window.localStorage.setItem(key, value);
  }
};

const deleteValue = async (key) => {
  if (isChromeExtension) {
    const removedValue = await chrome.storage.sync.remove(key);
    return removedValue;
  } else {
    window.localStorage.removeItem(key);
  }
};

const clearAll = async () => {
  if (isChromeExtension) {
    await chrome.storage.sync.clear();
  } else {
    for (let i = 0; i < storedValuesKeys.length; i++) {
      const key = storedValuesKeys[i];
      window.localStorage.removeItem(key);
    }
  }
};

/*********************************************************/
/*********************************************************/
/*******************  NESTED VALUES  *********************/
/*********************************************************/
/*********************************************************/
/*********************************************************/

const getNestedValue = async (parentKey, childKey) => {
  if (isChromeExtension) {
    const value = (await chrome.storage.sync.get(parentKey))[parentKey];
    if (value && value[childKey]) return value[childKey];
    else return null;
  } else {
    const value = JSON.parse(window.localStorage.getItem(parentKey));
    if (value && value[childKey]) return value[childKey];
    else return null;
  }
};

const setNestedValue = async (parentKey, childKey, newChildValue) => {
  if (isChromeExtension) {
    const parentObject = (await chrome.storage.sync.get(parentKey))[parentKey];
    let newParentObject = {};
    if (parentObject)
      newParentObject = { ...parentObject, [childKey]: newChildValue };
    else newParentObject = { [childKey]: newChildValue };

    await chrome.storage.sync.set({
      [parentKey]: newParentObject,
    });
  } else {
    const parentObject = JSON.parse(window.localStorage.getItem(parentKey));
    let newParentObject = {};
    if (parentObject)
      newParentObject = { ...parentObject, [childKey]: newChildValue };
    else newParentObject = { [childKey]: newChildValue };
    window.localStorage.setItem(parentKey, JSON.stringify(newParentObject));
  }
};

const deleteNestedValue = async (parentKey, childKey) => {
  if (isChromeExtension) {
    const rootObject = await chrome.storage.sync.get(parentKey);
    const newParent = { ...rootObject[parentKey] };
    delete newParent[childKey];
    await chrome.storage.sync.set({
      [parentKey]: newParent,
    });
  } else {
    const rootObject = JSON.parse(window.localStorage.getItem(parentKey));
    const newParent = { ...rootObject[parentKey] };
    delete newParent[childKey];
    window.localStorage.setItem(parentKey, JSON.stringify(newParent));
    return newParent;
  }
};

/*********************************************************/
/*********************************************************/
/*********************  GETTERS  ***********************/
/*********************************************************/
/*********************************************************/
/*********************************************************/

// null == false
const getIsExpandedPref = async () => {
  const isExpandedPref = await getValue('isExpandedPref');
  if (isExpandedPref && (isExpandedPref === true || isExpandedPref === 'true'))
    return true;
  return false;
};

// null == false
const getOktaOpenOnNext = async () => {
  const oktaOpenOnNext = await getValue('oktaOpenOnNext');
  if (oktaOpenOnNext && (oktaOpenOnNext === true || oktaOpenOnNext === 'true'))
    return true;
  return false;
};

// null == false
const getHasSeenDescriptionPage = async () => {
  const hasSeenDescriptionPage = await getValue('hasSeenDescriptionPage');
  if (
    hasSeenDescriptionPage &&
    (hasSeenDescriptionPage === true || hasSeenDescriptionPage === 'true')
  )
    return true;
  return false;
};

const getShowExtensionPrompt = async () => {
  const extensionPromptSnoozeTill = await getValue('extensionPromptSnoozeTill');
  if (
    !extensionPromptSnoozeTill ||
    dayjs(extensionPromptSnoozeTill).isBefore(dayjs())
  )
    return true;
  else return false;
};

// null == false
const getHasSeenWelcomeDoc = async (toolID, docID) => {
  const welcomeDocID = await getNestedValue('hasSeenWelcomeDoc', toolID);
  if (welcomeDocID && welcomeDocID === docID) return true;
  else return false;
};

// null == true
const getAreTriggersEnabledValue = async () => {
  const enabledValue = await getValue('notificationsEnabled');
  return (
    enabledValue == null || enabledValue === true || enabledValue === 'true'
  );
};

const getIsSnoozedOrDisabled = async (triggerId) => {  
  const triggerValue = await getNestedValue('triggers', triggerId);  
  if (triggerValue === 'disabled') {
    return true;
  }
  if (!triggerValue) {
    return false;
  } else if (triggerValue) {
    const triggerValueDate = dayjs(triggerValue);    
    if (dayjs().isBefore(triggerValueDate)) {
      return true;
    }
  }
  return false;
};

/*********************************************************/
/*********************************************************/
/*********************  SETTERS  ***********************/
/*********************************************************/
/*********************************************************/
/*********************************************************/

const snoozeExtensionPrompt = async (snoozeTime = 3) => {
  const snoozeTill = dayjs().add(snoozeTime, 'day')
  setValue('extensionPromptSnoozeTill', snoozeTill);
};

// WELCOME DOC UTILITY FUNCTIONS
const setHasSeenWelcomeDoc = async (toolID, docID) => {
  await setNestedValue('hasSeenWelcomeDoc', toolID, docID);
};

const removeHasSeenWelcomeDoc = async (toolID, docID) => {
  await deleteNestedValue('hasSeenWelcomeDoc', toolID, docID);
};

// TRIGGER UTILITY FUNCTIONS
const snoozeTrigger = (triggerId, snoozeTime = 1) => {
  const snoozeTill = dayjs().add(snoozeTime, 'day')
  setNestedValue('triggers', triggerId, snoozeTill);
};

const disableTrigger = (triggerId) => {
  setNestedValue('triggers', triggerId, 'disabled');
};

//  EXPORTS
const storageService = {
  logAll,
  getValue,
  setValue,
  deleteValue,
  deleteNestedValue,
  setNestedValue,
  getNestedValue,
  clearAll,
  getHasSeenWelcomeDoc,
  setHasSeenWelcomeDoc,
  removeHasSeenWelcomeDoc,
  snoozeTrigger,
  disableTrigger,
  getIsSnoozedOrDisabled,
  getAreTriggersEnabledValue,
  getIsExpandedPref,
  getOktaOpenOnNext,
  getHasSeenDescriptionPage,
  getShowExtensionPrompt,
  snoozeExtensionPrompt,
};

export default storageService;
